import React, { useEffect } from 'react';
import Button from '../Button';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

/**
 * Drawer that slides in from the left for the filter panel on mobile.
 */
export default function FilterDrawer({ isOpen, onClose, children, title = 'Filtreler' }: FilterDrawerProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="filter-drawer-overlay"
        role="button"
        tabIndex={-1}
        aria-label="Filtreyi kapat"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Enter' && onClose()}
      />
      <div
        className="filter-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom bg-white sticky-top">
          <span className="fw-bold text-primary">
            <i className="fas fa-filter me-2" />
            {title}
          </span>
          <Button
            type="button"
            className="btn btn-link text-dark p-2"
            onClick={onClose}
            aria-label="Filtreyi kapat"
          >
            <i className="fas fa-times fa-lg" />
          </Button>
        </div>
        <div className="filter-drawer-body overflow-auto p-3">
          {children}
        </div>
      </div>
      <style>
        {`
        .filter-drawer-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1040;
          animation: filter-drawer-fadeIn 0.2s ease;
        }
        .filter-drawer-panel {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: min(320px, 100vw);
          background: #fff;
          z-index: 1050;
          box-shadow: 4px 0 20px rgba(0,0,0,0.15);
          animation: filter-drawer-slideIn 0.25s ease;
          display: flex;
          flex-direction: column;
        }
        .filter-drawer-body {
          flex: 1;
          min-height: 0;
        }
        @keyframes filter-drawer-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes filter-drawer-slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        `}
      </style>
    </>
  );
}
